import React from 'react';
import axios from 'axios';
import { Check, CheckSquare, ChevronDown, Clock, Download, Edit3, Eraser, Film, GitMerge, Loader, MapPin, Play, Plus, RotateCcw, Sparkles, Square, Trash2, UserCheck, Volume2, Wrench } from 'lucide-react';
import ClearableField from './ClearableField';
import HistoricalDatePicker from './HistoricalDatePicker';
import Button from './ui/Button';
import Select from './ui/Select';
import { BLANK_EVENT, TIMELINE_CATEGORIES, resolveCategory } from '../constants/timeline';
import { NUDGES } from '../constants';
import { Z } from '../constants/tokens';
import { buildMp4Candidates, encodeVideoPath } from '../utils/videoUtils';
import { PlayButton } from './AudioClipPlayer';
import { getLanguageMeta } from '../constants/languages';
import LanguageSelector from './LanguageSelector';
import { useToast } from '../toastContext';
import { useConfirm } from '../confirmContext';
import { usePrompt } from '../promptContext';

export default function Timeline({ root, theme, backendUrl, videoId = '', activeSubdir = '', onSeekVideo = null, pushToast = null, logActivity = null, currentUser = null, onOpenMergeReview = null }) {
  const canEdit = currentUser?.role === 'admin' || currentUser?.role === 'editor';
  // Fallback for when Timeline is rendered without the pushToast prop.
  const { pushToast: toastFromContext } = useToast();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const [events, setEvents]     = React.useState([]);
  const [loading, setLoading]   = React.useState(true);
  const [editId, setEditId]     = React.useState(null);
  const [draft, setDraft]       = React.useState(BLANK_EVENT);
  const [saving, setSaving]     = React.useState(false);
  const [filter, setFilter]     = React.useState('all');
  const [zoom, setZoom]         = React.useState(1);
  const [fitToView, setFitToView] = React.useState(true);
  const [activePreviewEvent, setActivePreviewEvent] = React.useState(null);
  const [mergeSelection, setMergeSelection] = React.useState(new Set());
  const [merging, setMerging] = React.useState(false);
  const [aiReviewing, setAiReviewing] = React.useState(false);
  const [aiPreview, setAiPreview] = React.useState(null); // { original_count, preview_count, merged_count, preview }
  const [applying, setApplying] = React.useState(false);
  const [reverting, setReverting] = React.useState(false);
  const [backfilling, setBackfilling] = React.useState(false);
  const [toolsMenuOpen, setToolsMenuOpen] = React.useState(false);
  const toolsMenuRef = React.useRef(null);
  React.useEffect(() => {
    if (!toolsMenuOpen) return undefined;
    const onDown = (e) => { if (toolsMenuRef.current && !toolsMenuRef.current.contains(e.target)) setToolsMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [toolsMenuOpen]);
  const [activeNudge, setActiveNudge] = React.useState(0.5);
  const [subtitleLangs, setSubtitleLangs] = React.useState([]);
  const [subtitleLang, setSubtitleLang] = React.useState('en');
  // Track active tape/part — extract from videoId (e.g. "Name.pt2.sv" → 2)
  const defaultPart = React.useMemo(() => {
    const m = videoId.match(/\.pt(\d+)\./);
    return m ? parseInt(m[1], 10) : 1;
  }, [videoId]);
  const [activePart, setActivePart] = React.useState(defaultPart);
  React.useEffect(() => setActivePart(defaultPart), [defaultPart]);
  // Real rendered heights of the overview cards, keyed by year — see the layout
  // effect after overviewLayout.
  const [measuredCardHeights, setMeasuredCardHeights] = React.useState({});
  const overviewCardRefs = React.useRef({});
  const overviewPaneRef = React.useRef(null);
  const [overviewPaneWidth, setOverviewPaneWidth] = React.useState(0);
  const [leftPanelWidth, setLeftPanelWidth] = React.useState(() => {
    const saved = sessionStorage.getItem('gwv_timeline_left_w');
    return saved ? Number(saved) : 35;
  });
  const isDraggingRef = React.useRef(false);
  const splitContainerRef = React.useRef(null);
  const [topPanelPct, setTopPanelPct] = React.useState(() => {
    const saved = sessionStorage.getItem('gwv_timeline_top_pct');
    return saved ? Number(saved) : 35;
  });
  const leftPanelRef = React.useRef(null);
  const rightPanelRef = React.useRef(null);
  const videoRef = React.useRef(null);

  const [familyMembers, setFamilyMembers] = React.useState([]);
  const load = () => {
    setLoading(true);
    axios.get(`${backendUrl}/subject/timeline?root=${encodeURIComponent(root)}`)
      .then(r => setEvents(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    // Also fetch family members for birth/death overlay
    axios.get(`${backendUrl}/subject/family?root=${encodeURIComponent(root)}`)
      .then(r => setFamilyMembers(r.data || []))
      .catch(() => {});
  };
  React.useEffect(load, [root, backendUrl]);

  // Generate synthetic timeline events from family members, but skip if already in DB events
  const familyEvents = React.useMemo(() => {
    // Build a set of existing event signatures to avoid duplicates
    const existingKeys = new Set();
    for (const e of events) {
      // Normalize: "Adela Rose born" + year "2005" → "adela rose born|2005"
      if (e.title) existingKeys.add(`${e.title.toLowerCase().trim()}|${(e.year || '').toString().trim()}`);
    }
    const evts = [];
    for (const fm of familyMembers) {
      if (!fm.name || fm.relation === 'subject') continue;
      const rel = (fm.relation || '').replace(/_/g, ' ');
      if (fm.birth_year) {
        const key = `${fm.name.toLowerCase().trim()} born|${String(fm.birth_year).trim()}`;
        if (!existingKeys.has(key)) {
          evts.push({
            id: `fam-birth-${fm.id}`,
            _family: true,
            year: String(fm.birth_year),
            title: `${fm.name} born`,
            description: `${rel ? rel.charAt(0).toUpperCase() + rel.slice(1) : 'Family member'} born`,
            category: 'family',
            sort_key: parseFloat(fm.birth_year) || 9999,
          });
        }
      }
      if (fm.death_year) {
        const key = `${fm.name.toLowerCase().trim()} died|${String(fm.death_year).trim()}`;
        if (!existingKeys.has(key)) {
          evts.push({
            id: `fam-death-${fm.id}`,
            _family: true,
            year: String(fm.death_year),
            title: `${fm.name} died`,
            description: `${rel ? rel.charAt(0).toUpperCase() + rel.slice(1) : 'Family member'}${fm.fate && fm.fate !== 'unknown' ? ` — ${fm.fate}` : ''}`,
            category: 'family',
            sort_key: parseFloat(fm.death_year) || 9999,
          });
        }
      }
    }
    return evts;
  }, [familyMembers, events]);

  // Fetch available subtitle languages
  React.useEffect(() => {
    if (!root) return;
    axios.get(`${backendUrl}/translate/languages/${encodeURIComponent(root)}`)
      .then(r => {
        const langs = r.data?.languages || r.data || [];
        setSubtitleLangs(langs);
        // Default to English if available, else first language
        if (langs.includes('en')) setSubtitleLang('en');
        else if (langs.length) setSubtitleLang(langs[0]);
      })
      .catch(() => {});
  }, [root, backendUrl]);

  // Build the subtitle track URL — uses activePart for correct tape
  const subtitleTrackUrl = React.useMemo(() => {
    if (!videoId || subtitleLang === 'off') return '';
    const tok = sessionStorage.getItem('gwv_token') || '';
    const parts = videoId.split('.');
    const langSuffix = parts[parts.length - 1]; // e.g. "sv"

    if (subtitleLang === langSuffix) {
      return `${backendUrl}/vtt/${encodeURIComponent(videoId)}?part=${activePart}&token=${encodeURIComponent(tok)}`;
    }
    return `${backendUrl}/vtt/${encodeURIComponent(videoId)}?part=${activePart}&lang=${subtitleLang}&token=${encodeURIComponent(tok)}`;
  }, [videoId, subtitleLang, backendUrl, activePart]);

  const openNew  = () => { setDraft(BLANK_EVENT); setEditId('new'); };
  const openEdit = ev => { setDraft({ ...ev }); setEditId(ev.id); };
  const cancel   = () => { setEditId(null); setDraft(BLANK_EVENT); };

  const toPayload = React.useCallback((eventDraft) => ({
    year: String(eventDraft?.year || '').trim(),
    title: String(eventDraft?.title || '').trim(),
    description: String(eventDraft?.description || ''),
    category: String(eventDraft?.category || 'other'),
    location: String(eventDraft?.location || ''),
    part_number: Number.isFinite(eventDraft?.part_number) ? eventDraft.part_number : null,
    source_quote: String(eventDraft?.source_quote || ''),
    source_locator: String(eventDraft?.source_locator || ''),
    verified: Boolean(eventDraft?.verified),
  }), []);

  const onDividerPointerDown = React.useCallback((e) => {
    e.preventDefault();
    isDraggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      if (!isDraggingRef.current || !splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setLeftPanelWidth(Math.min(45, Math.max(15, pct)));
    };
    const onUp = () => {
      isDraggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      setLeftPanelWidth(w => { sessionStorage.setItem('gwv_timeline_left_w', String(w)); return w; });
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, []);

  const onVDividerPointerDown = React.useCallback((e) => {
    e.preventDefault();
    isDraggingRef.current = true;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      if (!isDraggingRef.current || !leftPanelRef.current) return;
      const rect = leftPanelRef.current.getBoundingClientRect();
      const pct = ((ev.clientY - rect.top) / rect.height) * 100;
      setTopPanelPct(Math.min(70, Math.max(15, pct)));
    };
    const onUp = () => {
      isDraggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      setTopPanelPct(w => { sessionStorage.setItem('gwv_timeline_top_pct', String(w)); return w; });
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, []);

  const save = () => {
    const payload = toPayload(draft);
    if (!payload.title || !payload.year) return;
    setSaving(true);
    const req = editId === 'new'
      ? axios.post(`${backendUrl}/subject/timeline?root=${encodeURIComponent(root)}`, payload)
      : axios.put(`${backendUrl}/subject/timeline/${editId}?root=${encodeURIComponent(root)}`, payload);
    req
      .then(() => { load(); cancel(); })
      .catch(err => {
        const msg = err?.response?.data?.detail || err?.message || 'Failed to save event';
        if (pushToast) pushToast('timeline-save-error', 'error', msg);
        else toastFromContext('timeline-save-error', 'error', msg);
      })
      .finally(() => setSaving(false));
  };

  const remove = async id => {
    if (!await confirm({ message: 'Delete this event?', title: 'Delete event?', confirmLabel: 'Delete', destructive: true })) return;
    axios.delete(`${backendUrl}/subject/timeline/${id}?root=${encodeURIComponent(root)}`)
      .then(() => { load(); cancel(); })
      .catch(err => {
        const msg = err?.response?.data?.detail || err?.message || 'Failed to delete event';
        if (pushToast) pushToast('timeline-delete-error', 'error', msg);
        else toastFromContext('timeline-delete-error', 'error', msg);
      });
  };

  const toggleVerify = ev => {
    axios.put(
      `${backendUrl}/subject/timeline/${ev.id}?root=${encodeURIComponent(root)}`,
      { ...toPayload(ev), verified: !ev.verified }
    ).then(load).catch(() => {});
  };

  const videoStopTimerRef = React.useRef(null);
  const seekVideoToLocator = React.useCallback((locator) => {
    if (!locator) return;
    // Parse "Tape N HH:MM:SS-HH:MM:SS" or "Tape N HH:MM:SS"
    const tapeMatch = locator.match(/Tape\s+(\d+)/i);
    const tapeNum = tapeMatch ? parseInt(tapeMatch[1], 10) : null;
    const times = [...locator.matchAll(/(\d{2}):(\d{2}):(\d{2})/g)];
    if (!times.length) return;
    const startSec = parseInt(times[0][1]) * 3600 + parseInt(times[0][2]) * 60 + parseInt(times[0][3]);
    const endSec = times.length > 1
      ? parseInt(times[1][1]) * 3600 + parseInt(times[1][2]) * 60 + parseInt(times[1][3])
      : null;

    // Switch tape if needed — video + VTT will re-render via activePart change
    if (tapeNum && tapeNum !== activePart) {
      setActivePart(tapeNum);
      // Wait for video to load then seek
      const waitAndSeek = () => {
        if (!videoRef.current) return;
        const onLoaded = () => {
          videoRef.current.removeEventListener('loadeddata', onLoaded);
          videoRef.current.currentTime = startSec;
          videoRef.current.play().catch(() => {});
          if (endSec && endSec > startSec) {
            if (videoStopTimerRef.current) clearInterval(videoStopTimerRef.current);
            videoStopTimerRef.current = setInterval(() => {
              if (videoRef.current && videoRef.current.currentTime >= endSec) {
                videoRef.current.pause();
                clearInterval(videoStopTimerRef.current);
                videoStopTimerRef.current = null;
              }
            }, 250);
          }
        };
        videoRef.current.addEventListener('loadeddata', onLoaded);
      };
      setTimeout(waitAndSeek, 50);
      return;
    }

    // Same tape — seek directly
    if (!videoRef.current) return;
    if (videoStopTimerRef.current) { clearInterval(videoStopTimerRef.current); videoStopTimerRef.current = null; }
    try {
      videoRef.current.currentTime = startSec;
      videoRef.current.play().catch(() => {});
      if (endSec && endSec > startSec) {
        videoStopTimerRef.current = setInterval(() => {
          if (videoRef.current && videoRef.current.currentTime >= endSec) {
            videoRef.current.pause();
            clearInterval(videoStopTimerRef.current);
            videoStopTimerRef.current = null;
          }
        }, 250);
      }
    } catch {}
  }, [activePart]);

  const _toast = React.useCallback((id, type, msg, label) => {
    if (pushToast) pushToast(id, type, msg);
    if (logActivity) logActivity(id, 'timeline', label || msg, type, type === 'error' ? { message: msg } : {});
  }, [pushToast, logActivity]);

  const eventListRef = React.useRef(null);
  // Overview card click → scroll left list to center
  const selectAndScrollToEvent = React.useCallback((item) => {
    setActivePreviewEvent(item);
    requestAnimationFrame(() => {
      const container = eventListRef.current;
      if (!container) return;
      const el = container.querySelector(`[data-event-id="${item.id}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, []);
  // Left card click → scroll overview to center the individual item
  const selectAndScrollToOverview = React.useCallback((item) => {
    setActivePreviewEvent(item);
    requestAnimationFrame(() => {
      const pane = overviewPaneRef.current;
      if (!pane) return;
      // First try to find the exact item, then fall back to group
      const el = pane.querySelector(`[data-overview-item-id="${item.id}"]`) ||
                 pane.querySelector(`[data-overview-id="${item.id}"]`);
      if (el) {
        const paneRect = pane.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const scrollLeft = pane.scrollLeft + (elRect.left - paneRect.left) - (paneRect.width / 2) + (elRect.width / 2);
        const scrollTop = pane.scrollTop + (elRect.top - paneRect.top) - (paneRect.height / 2) + (elRect.height / 2);
        pane.scrollTo({ left: Math.max(0, scrollLeft), top: Math.max(0, scrollTop), behavior: 'smooth' });
      }
    });
  }, []);

  const toggleMergeSelect = React.useCallback((id) => {
    setMergeSelection(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const handleMerge = React.useCallback(async () => {
    const ids = [...mergeSelection];
    if (ids.length < 2) return;
    const keepId = await prompt({
      title: 'Which event do you want to keep?',
      message: 'The others will be merged into it.',
      choices: ids.map(id => {
        const ev = events.find(e => e.id === id);
        return { value: id, label: ev ? `${ev.year} ${ev.title}` : `#${id}` };
      }),
      confirmLabel: 'Merge',
    });
    if (keepId == null) return;
    const mergeIds = ids.filter(id => id !== keepId);
    setMerging(true);
    try {
      await axios.post(`${backendUrl}/subject/timeline/merge?root=${encodeURIComponent(root)}`, { keep_id: keepId, merge_ids: mergeIds });
      _toast('timeline-merge', 'success', `Merged ${ids.length} events`, 'Events merged');
      setMergeSelection(new Set());
      load();
    } catch (err) {
      _toast('timeline-merge', 'error', `Merge failed: ${err?.response?.data?.detail || err.message}`, 'Merge failed');
    } finally {
      setMerging(false);
    }
  }, [mergeSelection, events, backendUrl, root, _toast, prompt]);

  const handleAiReview = React.useCallback(async () => {
    if (!await confirm({
      message: 'Send all timeline events to AI for review and consolidation?',
      title: 'Run AI review?',
      confirmLabel: 'Run AI Review',
      destructive: false,
    })) return;
    setAiReviewing(true);
    _toast('timeline-ai-review', 'running', 'AI reviewing timeline events...', 'AI Review started');
    try {
      // The review runs as a background job: a full timeline takes minutes and
      // the reverse proxy closes any request at 60s, so we poll instead of
      // holding the connection open.
      const start = await axios.post(`${backendUrl}/subject/timeline/ai-review?root=${encodeURIComponent(root)}`);
      const jobId = start.data?.job_id;
      if (!jobId) throw new Error('No job id returned');

      const POLL_MS = 3000;
      const MAX_MS = 15 * 60 * 1000;
      const t0 = Date.now();
      let data = null;
      for (;;) {
        await new Promise(r => setTimeout(r, POLL_MS));
        const st = await axios.get(`${backendUrl}/jobs/${jobId}`);
        if (st.data?.status === 'complete') { data = st.data.result; break; }
        if (st.data?.status === 'failed') throw new Error(st.data.error || 'AI review failed');
        if (Date.now() - t0 > MAX_MS) throw new Error('Timed out waiting for the AI review');
      }

      setAiPreview(data);
      _toast('timeline-ai-review', 'success',
        `AI suggests ${data.preview_count} events (${data.merged_count} merges from ${data.original_count} originals)`,
        'AI Review complete'
      );
    } catch (err) {
      _toast('timeline-ai-review', 'error', `AI Review failed: ${err?.response?.data?.detail || err.message}`, 'AI Review failed');
    } finally {
      setAiReviewing(false);
    }
  }, [backendUrl, root, _toast, confirm]);

  const handleApplyAiReview = React.useCallback(async () => {
    setApplying(true);
    try {
      await axios.post(`${backendUrl}/subject/timeline/ai-review/apply?root=${encodeURIComponent(root)}`);
      _toast('timeline-ai-apply', 'success', 'AI suggestions applied', 'AI Review applied');
      setAiPreview(null);
      load();
    } catch (err) {
      _toast('timeline-ai-apply', 'error', `Apply failed: ${err?.response?.data?.detail || err.message}`, 'Apply failed');
    } finally {
      setApplying(false);
    }
  }, [backendUrl, root, _toast]);

  const handleRevertAiReview = React.useCallback(async () => {
    setReverting(true);
    try {
      await axios.post(`${backendUrl}/subject/timeline/ai-review/revert?root=${encodeURIComponent(root)}`);
      _toast('timeline-ai-revert', 'success', 'Timeline reverted to pre-review state', 'AI Review reverted');
      setAiPreview(null);
      load();
    } catch (err) {
      _toast('timeline-ai-revert', 'error', `Revert failed: ${err?.response?.data?.detail || err.message}`, 'Revert failed');
    } finally {
      setReverting(false);
    }
  }, [backendUrl, root, _toast]);

  const handleBackfillVtt = React.useCallback(async () => {
    setBackfilling(true);
    _toast('timeline-backfill', 'running', 'Matching quotes to VTT timestamps...', 'VTT Backfill started');
    try {
      const res = await axios.post(`${backendUrl}/subject/timeline/backfill-vtt?root=${encodeURIComponent(root)}`);
      const d = res.data;
      if (d.updated > 0) {
        _toast('timeline-backfill', 'success', `Matched ${d.updated}/${d.total_events} events to VTT timestamps`, 'VTT Backfill complete');
        load();
      } else {
        _toast('timeline-backfill', 'warning', d.message || `No matches found (${d.total_events} events, ${d.vtt_parts || 0} VTT parts)`, 'VTT Backfill — no matches');
      }
    } catch (err) {
      _toast('timeline-backfill', 'error', `VTT Backfill failed: ${err?.response?.data?.detail || err.message}`, 'VTT Backfill failed');
    } finally {
      setBackfilling(false);
    }
  }, [backendUrl, root, _toast]);

  const [cleaning, setCleaning] = React.useState(false);
  const handleCleanupLocators = React.useCallback(async () => {
    setCleaning(true);
    _toast('timeline-cleanup', 'running', 'Cleaning up non-timestamp locator segments...', 'VTT Cleanup started');
    try {
      const res = await axios.post(`${backendUrl}/subject/timeline/cleanup-locators?root=${encodeURIComponent(root)}`);
      const d = res.data;
      if (d.cleaned > 0) {
        _toast('timeline-cleanup', 'success', `Cleaned ${d.cleaned} event(s) — removed non-timestamp segments`, 'VTT Cleanup complete');
        load();
      } else {
        _toast('timeline-cleanup', 'success', 'All locators already clean — nothing to remove', 'VTT Cleanup — already clean');
      }
    } catch (err) {
      _toast('timeline-cleanup', 'error', `Cleanup failed: ${err?.response?.data?.detail || err.message}`, 'VTT Cleanup failed');
    } finally {
      setCleaning(false);
    }
  }, [backendUrl, root, _toast]);

  const [showFamily, setShowFamily] = React.useState(true);
  const allEvents = React.useMemo(() => {
    if (!showFamily || familyEvents.length === 0) return events;
    return [...events, ...familyEvents].sort((a, b) => (a.sort_key || 9999) - (b.sort_key || 9999));
  }, [events, familyEvents, showFamily]);
  const visible = filter === 'all' ? allEvents : allEvents.filter(e => resolveCategory(e.category) === filter);

  // Parse first VTT timestamp from source_locator for sorting
  const _parseLocatorSec = React.useCallback((loc) => {
    if (!loc) return 99999;
    const m = loc.match(/(\d{2}):(\d{2}):(\d{2})/);
    if (!m) return 99999;
    return parseInt(m[1]) * 3600 + parseInt(m[2]) * 60 + parseInt(m[3]);
  }, []);

  const _parseTapeNum = React.useCallback((ev) => {
    // Use part_number if set, else parse from source_locator "Tape N ..."
    if (ev.part_number) return ev.part_number;
    const loc = ev.source_locator || '';
    const m = loc.match(/tape\s*(\d+)/i);
    return m ? parseInt(m[1]) : 999;
  }, []);

  // Sort visible events: tape number → VTT timestamp → sort_key (year)
  const sortedVisible = React.useMemo(() => {
    return [...visible].sort((a, b) => {
      const tapeA = _parseTapeNum(a), tapeB = _parseTapeNum(b);
      if (tapeA !== tapeB) return tapeA - tapeB;
      const secA = _parseLocatorSec(a.source_locator), secB = _parseLocatorSec(b.source_locator);
      if (secA !== secB) return secA - secB;
      return (a.sort_key || 0) - (b.sort_key || 0);
    });
  }, [visible, _parseTapeNum, _parseLocatorSec]);

  // Group events by decade for visual separation
  const grouped = React.useMemo(() => {
    const decades = {};
    sortedVisible.forEach(ev => {
      const yr = parseInt((ev.year || '0').replace('c.','').split('-')[0]) || 0;
      const decade = Math.floor(yr / 10) * 10;
      const key = decade > 0 ? `${decade}s` : 'Unknown';
      if (!decades[key]) decades[key] = { decade, events: [] };
      decades[key].events.push(ev);
    });
    return Object.values(decades).sort((a, b) => a.decade - b.decade);
  }, [sortedVisible]);

  const groupedDecadeCards = React.useMemo(() => {
    return grouped.map(({ decade, events: evts }) => {
      const buckets = new Map();
      evts.forEach(ev => {
        const key = ev.year || 'Unknown';
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(ev);
      });
      const cards = Array.from(buckets.entries()).map(([yearKey, bucket]) => ({
        yearKey,
        items: bucket,  // preserve tape→VTT sort order from sortedVisible
        sortYear: parseInt((yearKey || '0').replace('c.','').split('-')[0], 10) || 0,
      })).sort((a, b) => a.sortYear - b.sortYear || a.yearKey.localeCompare(b.yearKey));
      return { decade, cards };
    });
  }, [grouped]);

  const overviewEvents = React.useMemo(() => {
    return [...visible]
      .map(ev => {
        const year = parseInt((ev.year || '0').replace('c.','').split('-')[0], 10) || 0;
        return { ...ev, _sortYear: year };
      })
      .filter(ev => ev._sortYear > 0)
      .sort((a, b) => a._sortYear - b._sortYear);
  }, [visible]);

  const overviewGroups = React.useMemo(() => {
    // Bucket by year AND category. Bucketing on year alone put up to three
    // categories in one card — 1945 held camp, liberation and postwar together —
    // and the card took the colour of whichever item happened to sort first, so
    // the colour coding lied about most of its contents. One card is now one
    // category, which also breaks the biggest cards into smaller ones the packer
    // can place far more tidily.
    const buckets = new Map();
    overviewEvents.forEach(ev => {
      const cat = resolveCategory(ev.category);
      const key = `${ev._sortYear}|${cat}`;
      if (!buckets.has(key)) buckets.set(key, { sortYear: ev._sortYear, category: cat, items: [] });
      buckets.get(key).items.push(ev);
    });
    return Array.from(buckets.values())
      .map(g => ({ ...g, items: [...g.items].sort((a, b) => (a.title || '').localeCompare(b.title || '')) }))
      .sort((a, b) => a.sortYear - b.sortYear || a.category.localeCompare(b.category));
  }, [overviewEvents]);

  const overviewRange = React.useMemo(() => {
    if (!overviewEvents.length) return null;
    const years = overviewEvents.map(ev => ev._sortYear);
    const min = Math.min(...years);
    const max = Math.max(...years);
    return { min, max: max === min ? max + 1 : max };
  }, [overviewEvents]);
  const decadeMarkers = React.useMemo(() => {
    if (!overviewRange) return [];
    const start = Math.floor(overviewRange.min / 10) * 10;
    const end = Math.ceil(overviewRange.max / 10) * 10;
    const markers = [];
    for (let year = start; year <= end; year += 10) markers.push(year);
    return markers;
  }, [overviewRange]);
  const overviewBaseWidth = React.useMemo(() => {
    if (!overviewRange) return 960;
    const yearSpan = Math.max(1, overviewRange.max - overviewRange.min);
    // Crowding is absorbed vertically (cards stack in lanes), so the canvas no
    // longer needs a full card-width slot per group. It used to reserve 240px
    // each, which pushed the timeline past 5000px and left Fit at ~15% — every
    // card tiny and most of them off-screen.
    // Tuned by simulating the packer over this archive's year distribution:
    // every value below reaches zero overlaps, so the trade is width against
    // how many cards get pushed off their true year. Compressing past this
    // point doubles that drift, which is what makes a card feel lost.
    return Math.max(
      960,
      overviewGroups.length * 100,
      decadeMarkers.length * 150,
      yearSpan * 24
    );
  }, [decadeMarkers.length, overviewGroups.length, overviewRange]);
  const overviewViewportWidth = Math.max(0, overviewPaneWidth - 40);
  const fitScale = overviewViewportWidth > 0
    ? Math.min(1, Math.max(0.05, overviewViewportWidth / overviewBaseWidth))
    : 1;
  const overviewScale = fitToView ? fitScale : Math.max(0.05, zoom);
  const actualZoomPct = Math.round(overviewScale * 100);
  const overviewRenderedWidth = Math.max(overviewViewportWidth, overviewBaseWidth * overviewScale);
  const markerTop = 8;
  const markerBandHeight = 46;
  const timelineY = 154;
  const overviewLayout = React.useMemo(() => {
    if (!overviewRange || !overviewGroups.length) return [];

    const cardWidth = 176;
    const topStart = markerTop + markerBandHeight + 8;
    const bottomStart = timelineY + 28;
    const cardGapX = 26;
    const cardGapY = 18;
    const horizontalOffsets = [0, -60, 60, -120, 120, -180, 180, -240, 240];
    const placed = [];
    const placementsByIndex = {};
    const rectsOverlap = (a, b) => (
      a.left < b.right + cardGapX &&
      a.right > b.left - cardGapX &&
      a.top < b.bottom + cardGapY &&
      a.bottom > b.top - cardGapY
    );
    // Card height drives collision detection, so a bad guess means cards that
    // "don't overlap" on paper sit on top of each other on screen. Measured
    // heights (from the layout effect below) win; the estimate is only the
    // first-paint fallback and models what actually makes a card tall: titles
    // wrap at this width, and each locator adds a row of Audio/Video buttons.
    const CHARS_PER_LINE = 26;   // ~160px of content at 0.67rem
    const LINE_H = 14;
    const estimateHeight = (group) => {
      let h = 34;                // year pill + gap above the bordered box
      h += 12;                   // box padding
      group.items.forEach((item, i) => {
        const lines = Math.max(1, Math.ceil((item.title || '').length / CHARS_PER_LINE));
        h += lines * LINE_H;
        const locators = (item.source_locator || '').split(';').filter(s => s.trim()).length;
        if (locators) h += locators * 18;
        if (i > 0) h += 11;      // divider between items
      });
      return h;
    };
    const heightOf = (group) => measuredCardHeights[`${group.sortYear}|${group.category}`] || estimateHeight(group);
    const idealAnchors = overviewGroups.map(group => {
      const pct = ((group.sortYear - overviewRange.min) / (overviewRange.max - overviewRange.min)) * 100;
      return 50 + pct * ((overviewBaseWidth - 100) / 100);
    });
    const spreadAnchors = [...idealAnchors];
    for (let i = 1; i < spreadAnchors.length; i += 1) {
      const previousGroup = overviewGroups[i - 1];
      const currentGroup = overviewGroups[i];
      // Flat spacing: how MANY events a year holds makes its card taller, not
      // wider, so adding item count here only spread the timeline out. Cards
      // closer than a full width simply take different vertical lanes.
      const minSpacing = 92;
      spreadAnchors[i] = Math.max(spreadAnchors[i], spreadAnchors[i - 1] + minSpacing);
    }
    const overflow = spreadAnchors[spreadAnchors.length - 1] - (overviewBaseWidth - 88);
    if (overflow > 0) {
      for (let i = 0; i < spreadAnchors.length; i += 1) {
        const shift = overflow * (i / Math.max(1, spreadAnchors.length - 1));
        spreadAnchors[i] -= shift;
      }
    }
    const candidates = overviewGroups.map((group, idx) => {
      const cardHeight = heightOf(group);
      return {
        idx,
        group,
        cardHeight,
        idealX: Math.min(overviewBaseWidth - 88, Math.max(88, idealAnchors[idx])),
        anchorX: Math.min(overviewBaseWidth - 88, Math.max(88, spreadAnchors[idx])),
      };
    }).sort((a, b) => (
      b.cardHeight - a.cardHeight ||
      a.anchorX - b.anchorX
    ));

    candidates.forEach(({ idx, group, cardHeight, idealX, anchorX }) => {
      let placement = null;
      const preferredSides = cardHeight <= 128 ? [true, false] : [false, true];

      // Skyline packing. Fixed lanes cannot cope with these cards: a 13-event
      // year is ~600px tall, so it spans five 132px lanes and the search kept
      // failing over to horizontal drift. Instead, for each candidate column,
      // slide the card down until it clears whatever is already there, then
      // choose the candidate that scores best — shallow, and close to the year
      // it actually belongs to, since drifting sideways is what made cards feel
      // lost. Vertical stacking is preferred; sideways is the fallback.
      const columnTop = (centerX, startTop) => {
        let top = startTop;
        for (let guard = 0; guard < 60; guard += 1) {
          const rect = {
            left: centerX - (cardWidth / 2),
            right: centerX + (cardWidth / 2),
            top,
            bottom: top + cardHeight,
          };
          const hits = placed.filter(existing => rectsOverlap(rect, existing));
          if (!hits.length) return { top, rect };
          top = Math.max(...hits.map(h => h.bottom)) + cardGapY;
        }
        return null;
      };

      let best = null;
      for (const offset of horizontalOffsets) {
        const centerX = Math.min(
          overviewBaseWidth - 88,
          Math.max(88, anchorX + offset)
        );
        for (const placeOnTop of preferredSides) {
          const startTop = placeOnTop ? topStart : bottomStart;
          const found = columnTop(centerX, startTop);
          if (!found) continue;
          // Depth costs 1 per px; drifting off the year costs 2.5, so the packer
          // stacks rather than scatters unless stacking is much deeper.
          const score = (found.top - startTop) + (Math.abs(offset) * 2.5);
          if (!best || score < best.score) {
            best = { score, rect: found.rect, centerX, placeOnTop };
          }
        }
      }
      if (best) placement = { rect: best.rect, centerX: best.centerX, placeOnTop: best.placeOnTop };

      if (!placement) {
        // Last resort: drop below everything already placed rather than into a
        // fixed lane, which used to guarantee a collision once lanes ran out.
        const lowest = placed.reduce((m, r) => Math.max(m, r.bottom), bottomStart);
        const top = lowest + cardGapY;
        placement = {
          rect: {
            left: anchorX - (cardWidth / 2),
            right: anchorX + (cardWidth / 2),
            top,
            bottom: top + cardHeight,
          },
          centerX: anchorX,
          placeOnTop: false,
        };
      }

      placed.push(placement.rect);
      const connectorY = placement.placeOnTop ? placement.rect.top + 34 : placement.rect.top;
      placementsByIndex[idx] = {
        group,
        x: idealX,
        centerX: placement.centerX,
        top: placement.rect.top,
        bottom: placement.rect.bottom,
        lineTop: placement.placeOnTop ? connectorY : timelineY,
        lineBottom: placement.placeOnTop ? timelineY : connectorY,
        connectorY,
      };
    });

    return overviewGroups.map((_, idx) => placementsByIndex[idx]);
  }, [markerBandHeight, markerTop, measuredCardHeights, overviewBaseWidth, overviewGroups, overviewRange, timelineY]);

  // Second pass: measure what the cards actually came out as and, when that
  // differs from the estimate, feed the real heights back so the layout
  // re-runs against them. Without this the packing is only ever as good as the
  // guess, and titles wrap unpredictably.
  // Card height depends on content and the fixed 176px width, never on where the
  // card was placed, so this settles in two passes. The counter is only a guard
  // against an unforeseen oscillation locking up the browser; it resets whenever
  // the set of groups changes.
  const measurePassRef = React.useRef(0);
  const measureKeyRef = React.useRef('');
  React.useLayoutEffect(() => {
    const key = overviewGroups.map(g => `${g.sortYear}|${g.category}:${g.items.length}`).join(',');
    if (measureKeyRef.current !== key) { measureKeyRef.current = key; measurePassRef.current = 0; }
    if (measurePassRef.current > 4) return;

    const next = {};
    let changed = false;
    Object.entries(overviewCardRefs.current).forEach(([year, el]) => {
      if (!el) return;
      const h = el.offsetHeight;
      if (!h) return;
      next[year] = h;
      if (Math.abs((measuredCardHeights[year] || 0) - h) > 2) changed = true;
    });
    // Also re-run if a card disappeared, so stale heights cannot pin the layout.
    if (!changed && Object.keys(next).length !== Object.keys(measuredCardHeights).length) changed = true;
    if (changed) { measurePassRef.current += 1; setMeasuredCardHeights(next); }
  });
  const overviewHeight = React.useMemo(() => {
    if (!overviewLayout.length) return timelineY + 110;
    return Math.max(timelineY + 110, ...overviewLayout.map(item => item.bottom + 28));
  }, [overviewLayout, timelineY]);

  const inputStyle = { width: '100%', padding: '6px 8px', background: theme.inputBg || theme.card, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '6px', fontSize: '0.8rem', outline: 'none', boxSizing: 'border-box' };
  const labelStyle = { fontSize: '0.6rem', fontWeight: 700, color: theme.textSub, display: 'block', marginBottom: '3px', textTransform: 'uppercase' };

  const renderEventEditor = (mode = 'inline') => (
    <div style={{
      background: mode === 'inline' ? 'transparent' : theme.card,
      border: mode === 'inline' ? 'none' : `1.5px solid ${theme.glossaryAccent}`,
      borderRadius: '10px',
      padding: mode === 'inline' ? '0' : '14px',
    }}>
      {mode !== 'inline' && (
        <div style={{ fontWeight: 700, fontSize: '0.75rem', color: theme.glossaryAccent, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {editId === 'new' ? 'New Event' : 'Edit Event'}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '8px', marginBottom: '8px' }}>
        <div>
          <HistoricalDatePicker
            label="Date *"
            value={draft.year ?? ''}
            onChange={v => setDraft(d => ({ ...d, year: v }))}
            theme={theme}
            minYear={1860}
            maxYear={new Date().getFullYear()}
            showSummary={false}
          />
          <div style={{ fontSize: '0.58rem', color: theme.textSub, marginTop: '-8px' }}>Use year, month, and day when known.</div>
        </div>
        <div>
          <label style={labelStyle}>Event Title *</label>
          <ClearableField theme={theme} value={draft.title ?? ''} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} onClear={() => setDraft(d => ({ ...d, title: '' }))}
            placeholder="e.g. Deportation to Auschwitz-Birkenau" style={inputStyle} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
        <div>
          <label style={labelStyle}>Category</label>
          <Select theme={theme} value={draft.category} onChange={e => setDraft(d => ({ ...d, category: e.target.value }))}>
            {Object.entries(TIMELINE_CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </Select>
        </div>
        <div>
          <label style={labelStyle}>Location</label>
          <ClearableField theme={theme} value={draft.location ?? ''} onChange={e => setDraft(d => ({ ...d, location: e.target.value }))} onClear={() => setDraft(d => ({ ...d, location: '' }))}
            placeholder="e.g. Będzin, Poland" style={inputStyle} />
        </div>
      </div>
      <div style={{ marginBottom: '8px' }}>
        <label style={labelStyle}>Description</label>
        <ClearableField as="textarea" theme={theme} value={draft.description} onChange={e => setDraft(d => ({ ...d, description: e.target.value }))} onClear={() => setDraft(d => ({ ...d, description: '' }))}
          rows={4} placeholder="One or two sentences describing this event…" style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }} />
      </div>
      <div style={{ marginBottom: '8px' }}>
        <label style={labelStyle}>Source Quote (optional)</label>
        <ClearableField theme={theme} value={draft.source_quote ?? ''} onChange={e => setDraft(d => ({ ...d, source_quote: e.target.value }))} onClear={() => setDraft(d => ({ ...d, source_quote: '' }))}
          placeholder="Short verbatim quote from transcript anchoring this event…" style={inputStyle} />
      </div>
      <div style={{ marginBottom: '10px' }}>
        <label style={labelStyle}>Source Locator (VTT timestamps)</label>
        <ClearableField as="textarea" theme={theme} value={draft.source_locator ?? ''} onChange={e => setDraft(d => ({ ...d, source_locator: e.target.value }))} onClear={() => setDraft(d => ({ ...d, source_locator: '' }))}
          rows={2} placeholder="e.g. Tape 1 00:10:57-00:11:01; Tape 2 00:05:30-00:06:12" style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '0.75rem', resize: 'vertical' }} />
        <div style={{ fontSize: '0.55rem', color: theme.textSub, marginTop: '2px' }}>Separate multiple timestamps with semicolons</div>
        {/* ── Nudge editor for each locator segment ── */}
        {(() => {
          const locators = (draft.source_locator || '').split(';').map(s => s.trim()).filter(Boolean);
          if (!locators.length) return null;
          const parseHMS = (str) => {
            const m = str.match(/(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?/);
            if (!m) return null;
            return parseInt(m[1]) * 3600 + parseInt(m[2]) * 60 + parseInt(m[3]) + (m[4] ? parseInt(m[4].padEnd(3, '0')) / 1000 : 0);
          };
          const fmtHMS = (sec) => {
            if (sec == null || isNaN(sec)) return '00:00:00.000';
            const h = Math.floor(sec / 3600); const mn = Math.floor((sec % 3600) / 60); const s = sec % 60;
            return `${String(h).padStart(2, '0')}:${String(mn).padStart(2, '0')}:${s.toFixed(3).padStart(6, '0')}`;
          };
          const updateLocator = (idx, newVal) => {
            const parts = (draft.source_locator || '').split(';').map(s => s.trim());
            parts[idx] = newVal;
            setDraft(d => ({ ...d, source_locator: parts.join('; ') }));
          };
          const nudgeLocator = (idx, field, delta) => {
            const loc = locators[idx];
            const times = [...loc.matchAll(/(\d{2}:\d{2}:\d{2}(?:\.\d+)?)/g)].map(m => m[1]);
            if (!times.length) return;
            let startSec = parseHMS(times[0]);
            let endSec = times.length > 1 ? parseHMS(times[1]) : null;
            if (field === 'start' && startSec != null) startSec = Math.max(0, Math.round((startSec + delta) * 1000) / 1000);
            if (field === 'end' && endSec != null) endSec = Math.max(0, Math.round((endSec + delta) * 1000) / 1000);
            // Rebuild locator preserving prefix (e.g. "Tape 1")
            const prefix = loc.replace(/\d{2}:\d{2}:\d{2}(?:\.\d+)?.*$/, '').trim();
            const newLoc = endSec != null
              ? `${prefix} ${fmtHMS(startSec)}-${fmtHMS(endSec)}`.trim()
              : `${prefix} ${fmtHMS(startSec)}`.trim();
            updateLocator(idx, newLoc);
          };
          return (
            <div style={{ marginTop: '8px', padding: '8px', background: `${theme.border}22`, borderRadius: '8px' }}>
              {currentUser?.role === 'admin' && (
                <div style={{ fontSize: '0.55rem', color: theme.textSub, marginBottom: '6px', lineHeight: 1.5, padding: '4px 6px', background: `${theme.border}33`, borderRadius: '5px' }}>
                  <strong>Timestamp Editor</strong> — Use nudge buttons to fine-tune start/end times. Changes update the source locator text above. Prompt caching is active on all AI calls (system prompts cached for 5 min, 90% savings on repeated calls). Audio pill plays the clip; Video pill seeks the player.
                </div>
              )}
              {/* Nudge step selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <span style={{ fontSize: '0.6rem', color: theme.textSub, fontWeight: 700, letterSpacing: '0.05em' }}>NUDGE</span>
                {NUDGES.map(n => (
                  <button key={n.v} onClick={() => setActiveNudge(n.v)} style={{
                    padding: '2px 8px', fontSize: '0.65rem', fontWeight: 700, border: 'none', borderRadius: '4px', cursor: 'pointer',
                    background: activeNudge === n.v ? '#ff9f0a' : theme.card,
                    color: activeNudge === n.v ? '#fff' : theme.textSub,
                  }}>{n.label}</button>
                ))}
              </div>
              {locators.map((loc, idx) => {
                const times = [...loc.matchAll(/(\d{2}:\d{2}:\d{2}(?:\.\d+)?)/g)].map(m => m[1]);
                const startSec = times[0] ? parseHMS(times[0]) : null;
                const endSec = times[1] ? parseHMS(times[1]) : null;
                const prefix = loc.replace(/\d{2}:\d{2}:\d{2}(?:\.\d+)?.*$/, '').trim();
                if (startSec == null) return (
                  <div key={idx} style={{ fontSize: '0.65rem', color: theme.textSub, marginBottom: '4px', fontFamily: 'monospace' }}>{loc}</div>
                );
                const btnStyle = { padding: '2px 7px', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '4px', cursor: 'pointer', color: theme.text, fontWeight: 700, fontSize: '0.75rem' };
                const tsStyle = { fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 700, color: '#ff9f0a', background: theme.inputBg || theme.card, border: '1px solid #ff9f0a44', borderRadius: '4px', padding: '2px 6px', width: '105px', textAlign: 'center' };
                return (
                  <div key={idx} style={{ marginBottom: idx < locators.length - 1 ? '8px' : 0, paddingBottom: idx < locators.length - 1 ? '8px' : 0, borderBottom: idx < locators.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
                    {prefix && <div style={{ fontSize: '0.6rem', fontWeight: 700, color: theme.textSub, marginBottom: '4px' }}>{prefix}</div>}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '4px' }}>
                      <span style={{ width: '34px', fontSize: '0.6rem', fontWeight: 700, color: theme.textSub }}>START</span>
                      <button onClick={e => { e.stopPropagation(); nudgeLocator(idx, 'start', -activeNudge); }} style={btnStyle}>−</button>
                      <input value={fmtHMS(startSec)} readOnly style={tsStyle} />
                      <button onClick={e => { e.stopPropagation(); nudgeLocator(idx, 'start', +activeNudge); }} style={btnStyle}>+</button>
                    </div>
                    {endSec != null && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span style={{ width: '34px', fontSize: '0.6rem', fontWeight: 700, color: theme.textSub }}>END</span>
                        <button onClick={e => { e.stopPropagation(); nudgeLocator(idx, 'end', -activeNudge); }} style={btnStyle}>−</button>
                        <input value={fmtHMS(endSec)} readOnly style={tsStyle} />
                        <button onClick={e => { e.stopPropagation(); nudgeLocator(idx, 'end', +activeNudge); }} style={btnStyle}>+</button>
                      </div>
                    )}
                    {/* Preview play buttons — uses current draft timestamps without saving */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '5px' }}>
                      <span style={{ width: '34px', fontSize: '0.6rem', fontWeight: 700, color: theme.textSub }}>PLAY</span>
                      <PlayButton sourceLocator={locators[idx]} theme={theme} size="small" label="Audio" />
                      <button
                        onClick={e => { e.stopPropagation(); seekVideoToLocator(locators[idx]); }}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '6px', fontSize: '0.5rem', fontWeight: 700, cursor: 'pointer', color: '#ef4444', background: '#ef444414', border: '1px solid #ef444433' }}
                        title="Preview in video player">
                        <Play size={10} strokeWidth={2.5} />
                        Video
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button onClick={save} disabled={saving || !draft.title.trim() || !draft.year.trim()}
          style={{ flex: 1, padding: '8px', background: theme.glossaryAccent, color: '#fff', border: 'none', borderRadius: '7px', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Saving…' : editId === 'new' ? 'Add Event' : 'Save Changes'}
        </button>
        <Button variant="outline" onClick={cancel} theme={theme}>Cancel</Button>
      </div>
      {editId !== 'new' && editId !== null && (
        <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: `1px solid ${theme.border}` }}>
          <Button variant="danger-ghost" icon={Trash2} onClick={() => remove(editId)} theme={theme}>
            Delete Event
          </Button>
        </div>
      )}
    </div>
  );

  React.useEffect(() => {
    setActivePreviewEvent(null);
  }, [root, filter]);
  React.useEffect(() => {
    if (!overviewPaneRef.current || typeof ResizeObserver === 'undefined') return;
    const node = overviewPaneRef.current;
    const update = () => setOverviewPaneWidth(node.getBoundingClientRect().width || 0);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Scroll-wheel zoom on overview pane (Ctrl/Cmd + wheel or pinch)
  const overviewScaleRef = React.useRef(overviewScale);
  overviewScaleRef.current = overviewScale;
  React.useEffect(() => {
    const node = overviewPaneRef.current;
    if (!node) return;
    const handler = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.15 : 0.15;
      setFitToView(prev => {
        if (prev) setZoom(overviewScaleRef.current);
        return false;
      });
      setZoom(z => Math.min(20, Math.max(0.05, +((z === 1 && overviewScaleRef.current !== 1 ? overviewScaleRef.current : z) + delta).toFixed(2))));
    };
    node.addEventListener('wheel', handler, { passive: false });
    return () => node.removeEventListener('wheel', handler);
  }, []);

  const videoCandidates = React.useMemo(() => {
    if (!videoId) return [];
    // Swap part number in videoId to match activePart
    const adjusted = videoId.replace(/\.pt\d+\./, `.pt${activePart}.`);
    return buildMp4Candidates(adjusted);
  }, [videoId, activePart]);
  const exportPdf = React.useCallback(() => {
    const win = window.open('', '_blank', 'width=1280,height=900');
    if (!win) return;
    const printableWidth = 980;
    const printableHeight = 660;
    const printScale = Math.min(
      printableWidth / Math.max(overviewBaseWidth, 1),
      printableHeight / Math.max(overviewHeight, 1),
      1
    );
    const wrapSvgLine = (text, maxChars = 28) => {
      if (!text) return [];
      const words = String(text).split(/\s+/).filter(Boolean);
      const lines = [];
      let current = '';
      words.forEach(word => {
        const next = current ? `${current} ${word}` : word;
        if (next.length > maxChars && current) {
          lines.push(current);
          current = word;
        } else {
          current = next;
        }
      });
      if (current) lines.push(current);
      return lines.slice(0, 4);
    };
    const timelineOverviewHtml = overviewRange ? `
      <section class="timeline-sheet">
        <div class="timeline-title-row">
          <div>
            <h1>${root}</h1>
            <div class="subhead">Timeline overview</div>
          </div>
          <div class="legend">${visible.length} visible events</div>
        </div>
        <div class="timeline-stage" style="width:${overviewBaseWidth * printScale}px;height:${overviewHeight * printScale}px;">
          <div class="timeline-canvas" style="width:${overviewBaseWidth}px;height:${overviewHeight}px;transform:scale(${printScale});">
            <svg class="timeline-svg" viewBox="0 0 ${overviewBaseWidth} ${overviewHeight}">
              <line x1="50" y1="${timelineY}" x2="${overviewBaseWidth - 50}" y2="${timelineY}" stroke="#cbd5e1" stroke-width="3" stroke-linecap="round" />
              ${decadeMarkers.map(year => {
                const pct = ((year - overviewRange.min) / (overviewRange.max - overviewRange.min)) * 100;
                const x = 50 + pct * ((overviewBaseWidth - 100) / 100);
                return `
                  <line x1="${x}" y1="${markerTop + 22}" x2="${x}" y2="${timelineY - 8}" stroke="#94a3b8" stroke-width="1.5" stroke-dasharray="3 5" opacity="0.5" />
                `;
              }).join('')}
              ${overviewLayout.map(({ group, x, centerX, lineTop, lineBottom, connectorY }) => {
                const cfg = TIMELINE_CATEGORIES[group.category] || TIMELINE_CATEGORIES.other;
                return `
                  <line x1="${x}" y1="${lineTop}" x2="${x}" y2="${lineBottom}" stroke="${cfg.color}" stroke-width="2.5" stroke-linecap="round" />
                  ${centerX !== x ? `<line x1="${x}" y1="${connectorY}" x2="${centerX}" y2="${connectorY}" stroke="${cfg.color}" stroke-width="2.5" stroke-linecap="round" />` : ''}
                  <circle cx="${x}" cy="${timelineY}" r="6" fill="${cfg.color}" stroke="#ffffff" stroke-width="2" />
                `;
              }).join('')}
            </svg>
            ${decadeMarkers.map(year => {
              const pct = ((year - overviewRange.min) / (overviewRange.max - overviewRange.min)) * 100;
              const x = 50 + pct * ((overviewBaseWidth - 100) / 100);
              return `
                <div class="decade-pill" style="left:${x}px;top:${markerTop}px;">
                  ${year}s
                </div>
              `;
            }).join('')}
            ${overviewLayout.map(({ group, centerX, top }) => {
              const cfg = TIMELINE_CATEGORIES[group.category] || TIMELINE_CATEGORIES.other;
              return `
                <div class="overview-card" style="left:${centerX}px;top:${top}px;">
                  <div class="overview-pill" style="background:linear-gradient(${cfg.bg},${cfg.bg}),#ffffff;color:${cfg.color};border-color:${cfg.color}55;">
                    <span>${group.sortYear}</span>
                    <span>${group.items.length === 1 ? cfg.label : `${group.items.length} Events`}</span>
                  </div>
                  <div class="overview-card-body" style="border-color:${cfg.color}55;">
                    ${group.items.map((item, idx) => `
                      <div class="overview-item ${idx > 0 ? 'with-divider' : ''}" style="border-top-color:${cfg.color}30;">${item.title}</div>
                    `).join('')}
                  </div>
                </div>
              `;
            }).join('')}
            <svg class="timeline-svg timeline-svg-over" viewBox="0 0 ${overviewBaseWidth} ${overviewHeight}">
              ${overviewLayout.map(({ group, x, centerX, connectorY }) => {
                if (centerX === x) return '';
                const cfg = TIMELINE_CATEGORIES[group.category] || TIMELINE_CATEGORIES.other;
                return `<line x1="${x}" y1="${connectorY}" x2="${centerX}" y2="${connectorY}" stroke="${cfg.color}" stroke-width="2.5" stroke-linecap="round" stroke-dasharray="1 6" opacity="0.75" />`;
              }).join('')}
            </svg>
          </div>
        </div>
      </section>
    ` : '';
    const sections = groupedDecadeCards.map(({ decade, cards }) => `
      <section class="decade">
        <h2>${decade > 0 ? `${decade}s` : 'Unknown date'}</h2>
        ${cards.map(card => `
          <article class="card">
            <div class="meta">${card.yearKey}</div>
            ${card.items.map(item => {
              const cfg = TIMELINE_CATEGORIES[resolveCategory(item.category)] || TIMELINE_CATEGORIES.other;
              return `
                <div class="item">
                  <div class="title-row">
                    <h3>${item.title}</h3>
                    <span class="badge" style="background:${cfg.bg};color:${cfg.color};border-color:${cfg.color}55">${cfg.label}</span>
                  </div>
                  ${item.location ? `<div class="sub">${item.location}</div>` : ''}
                  ${item.description ? `<div class="body">${item.description}</div>` : ''}
                  ${item.source_quote ? `<blockquote>${item.source_quote}</blockquote>` : ''}
                </div>
              `;
            }).join('')}
          </article>
        `).join('')}
      </section>
    `).join('');
    win.document.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${root} Timeline</title>
  <style>
    @page timeline-sheet { size: 11in 8.5in landscape; margin: 0.35in; }
    @page detail-sheet { size: 8.5in 11in portrait; margin: 0.45in; }
    body { font-family: Georgia, serif; margin: 0; color: #111827; }
    h1 { margin: 0 0 6px; font-size: 28px; }
    .subhead { color: #6b7280; margin-bottom: 18px; font: 600 12px/1.4 system-ui, sans-serif; text-transform: uppercase; letter-spacing: 0.08em; }
    .timeline-sheet { page: timeline-sheet; break-after: page; padding: 0; }
    .timeline-title-row { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px; }
    .legend { font: 700 11px/1.2 system-ui, sans-serif; color: #64748b; text-transform: uppercase; letter-spacing: 0.08em; padding-top: 8px; }
    .timeline-stage { overflow: hidden; }
    .timeline-canvas { position: relative; transform-origin: top left; }
    .timeline-svg { position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible; z-index: 0; }
    /* Redraws the sideways connector runs as dots on top of the year labels. */
    .timeline-svg-over { z-index: 2; }
    .decade-pill {
      position: absolute;
      transform: translateX(-50%);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 4px 10px;
      border-radius: 999px;
      background: rgba(148,163,184,0.16);
      color: #64748b;
      font: 800 10px/1.1 system-ui, sans-serif;
      border: 1px solid #cbd5e1;
      white-space: nowrap;
    }
    .overview-card {
      position: absolute;
      transform: translateX(-50%);
      width: 176px;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      z-index: 1;
    }
    .overview-pill {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 5px;
      padding: 4px 10px;
      border-radius: 999px;
      font: 800 10px/1.1 system-ui, sans-serif;
      white-space: nowrap;
      max-width: 176px;
      overflow: hidden;
      text-overflow: ellipsis;
      margin: 0 auto;
      border: 1px solid transparent;
    }
    .overview-card-body {
      margin-top: 6px;
      width: 100%;
      background: #ffffff;
      border: 1px solid #cbd5e1;
      border-radius: 10px;
      padding: 6px 8px;
      box-sizing: border-box;
    }
    .overview-item {
      font: 700 10px/1.25 system-ui, sans-serif;
      color: #111827;
      text-align: center;
      padding-top: 0;
    }
    .overview-item.with-divider {
      border-top: 1px solid #e5e7eb;
      margin-top: 5px;
      padding-top: 5px;
    }
    .detail-sheet { page: detail-sheet; padding: 0.05in 0; }
    .detail-columns { column-count: 3; column-gap: 0.22in; }
    .decade { margin-bottom: 24px; break-inside: avoid; }
    .decade h2 { font: 700 12px/1.2 system-ui, sans-serif; text-transform: uppercase; letter-spacing: 0.12em; color: #64748b; border-bottom: 1px solid #cbd5e1; padding-bottom: 6px; margin-bottom: 12px; }
    .card { border: 1px solid #cbd5e1; border-radius: 10px; padding: 12px 14px; margin-bottom: 10px; break-inside: avoid; }
    .meta { font: 700 12px/1.2 system-ui, sans-serif; color: #475569; margin-bottom: 8px; }
    .item + .item { border-top: 1px solid #e5e7eb; margin-top: 10px; padding-top: 10px; }
    .title-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
    h3 { margin: 0; font: 700 17px/1.25 system-ui, sans-serif; }
    .badge { display: inline-flex; align-items: center; padding: 3px 9px; border-radius: 999px; border: 1px solid transparent; font: 700 11px/1.2 system-ui, sans-serif; white-space: nowrap; }
    .sub { color: #64748b; font: 500 12px/1.4 system-ui, sans-serif; margin-top: 4px; }
    .body { margin-top: 8px; line-height: 1.55; font-size: 14px; }
    blockquote { margin: 8px 0 0; padding-left: 10px; border-left: 2px solid #cbd5e1; color: #475569; font-style: italic; }
  </style>
</head>
<body>
  ${timelineOverviewHtml}
  <section class="detail-sheet">
    <h1>${root}</h1>
    <div class="subhead">Timeline details</div>
    <div class="detail-columns">
      ${sections}
    </div>
  </section>
</body>
</html>`);
    win.document.close();
    win.focus();
    window.setTimeout(() => win.print(), 250);
  }, [groupedDecadeCards, root]);

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '30px', color: theme.textSub, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
      <Loader size={22} strokeWidth={1.5} style={{ animation: 'spin 1s linear infinite' }} />
      <span style={{ fontSize: '0.8rem' }}>Loading timeline…</span>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', overflow: 'hidden', gap: '10px', boxSizing: 'border-box' }}>
      {events.length === 0 && editId === null ? (
        /* When there is no timeline data, the whole tab is a single clean empty
           state rather than a small video panel beside a blank visualization. */
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '14px', padding: '40px 24px' }}>
          <div style={{ width: '58px', height: '58px', borderRadius: '999px', background: theme.card, border: `1px solid ${theme.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Clock size={24} strokeWidth={1.6} style={{ color: theme.textSub }} />
          </div>
          <div style={{ fontSize: '1.05rem', fontWeight: 700, color: theme.text, fontFamily: "'Cormorant Garamond', Georgia, serif" }}>No timeline yet</div>
          <div style={{ fontSize: '0.82rem', color: theme.textSub, maxWidth: '440px', lineHeight: 1.55 }}>
            This interview has no timeline events. Add one manually, or run <strong>Extract from Transcript</strong> on the Biography tab to build it automatically.
          </div>
          {canEdit && (
            <Button variant="primary" icon={Plus} onClick={openNew} theme={theme}>Add Event</Button>
          )}
        </div>
      ) : (<>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {canEdit && (
            <button onClick={openNew} style={{ padding: '7px 12px', background: 'none', color: theme.glossaryAccent, border: `1.5px dashed ${theme.glossaryAccent}`, borderRadius: '7px', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Plus size={12} strokeWidth={2.5} /> Add Event
            </button>
          )}
          {events.length > 0 && (
            <Button variant="secondary" size="sm" icon={Download} onClick={exportPdf} theme={theme}>
              Export PDF
            </Button>
          )}
          {/* Event-data tools — a "Tools" menu; these act on the timeline events (not the transcript) */}
          {canEdit && events.length > 0 && (aiPreview ? (
            <>
              <button onClick={handleApplyAiReview} disabled={applying}
                style={{ padding: '6px 11px', background: '#34c759', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', opacity: applying ? 0.6 : 1 }}>
                <Check size={12} strokeWidth={2.5} /> {applying ? 'Applying...' : 'Approve AI'}
              </button>
              <button onClick={handleRevertAiReview} disabled={reverting}
                style={{ padding: '6px 11px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', opacity: reverting ? 0.6 : 1 }}>
                <RotateCcw size={12} strokeWidth={2} /> {reverting ? 'Reverting...' : 'Revert'}
              </button>
            </>
          ) : (
            <div ref={toolsMenuRef} style={{ position: 'relative' }}>
              <button onClick={() => setToolsMenuOpen(o => !o)} title="Timeline event tools"
                style={{ padding: '7px 12px', background: theme.card, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '7px', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Wrench size={12} strokeWidth={2} /> Tools
                <ChevronDown size={12} strokeWidth={2.5} style={{ transform: toolsMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
              </button>
              {toolsMenuOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, minWidth: '230px', background: theme.sidebar, border: `1px solid ${theme.border}`, borderRadius: '10px', boxShadow: '0 12px 32px rgba(0,0,0,0.22)', zIndex: Z.dropdown, padding: '4px' }}>
                  {[
                    { label: 'Stamp Times', busyLabel: 'Matching…', busy: backfilling, icon: Film, accent: '#f59e0b', desc: 'Match events to VTT timestamps for playback', onClick: handleBackfillVtt },
                    { label: 'Clean Locators', busyLabel: 'Cleaning…', busy: cleaning, icon: Eraser, accent: '#8b5cf6', desc: 'Remove locators without a timestamp', onClick: handleCleanupLocators },
                    { label: 'AI Review', busyLabel: 'Reviewing…', busy: aiReviewing, icon: Sparkles, accent: '#8b5cf6', desc: 'Dedupe & merge events with AI', onClick: handleAiReview },
                    ...(onOpenMergeReview ? [{ label: 'Review AI Merges', busyLabel: '', busy: false, icon: GitMerge, accent: '#34c759', desc: 'Compare merged events to the originals', onClick: onOpenMergeReview }] : []),
                  ].map(item => (
                    <button key={item.label} disabled={item.busy}
                      onClick={() => { setToolsMenuOpen(false); item.onClick(); }}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '9px', padding: '8px 9px', background: 'none', border: 'none', borderRadius: '7px', cursor: item.busy ? 'default' : 'pointer', textAlign: 'left', opacity: item.busy ? 0.6 : 1 }}
                      onMouseEnter={e => { if (!item.busy) e.currentTarget.style.background = `${theme.glossaryAccent}12`; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                      <span style={{ width: 28, height: 28, borderRadius: '8px', background: `${item.accent}1f`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {item.busy ? <Loader size={14} strokeWidth={2} color={item.accent} style={{ animation: 'spin 1s linear infinite' }} /> : <item.icon size={14} strokeWidth={2} color={item.accent} />}
                      </span>
                      <span style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: theme.text }}>{item.busy ? item.busyLabel : item.label}</div>
                        <div style={{ fontSize: '0.63rem', color: theme.textSub, lineHeight: 1.3 }}>{item.desc}</div>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        {events.length > 0 && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            {['all', ...Object.keys(TIMELINE_CATEGORIES)].map(cat => {
              const cfg = TIMELINE_CATEGORIES[cat];
              const isActive = filter === cat;
              const count = cat === 'all' ? allEvents.length : allEvents.filter(e => resolveCategory(e.category) === cat).length;
              if (cat !== 'all' && count === 0) return null;
              return (
                <button key={cat} onClick={() => setFilter(cat)}
                  style={{ padding: '3px 9px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '0.65rem', fontWeight: 700,
                    background: isActive ? (cfg?.color || theme.glossaryAccent) : theme.card,
                    color: isActive ? '#fff' : (cfg?.color || theme.textSub),
                  }}>
                  {cat === 'all' ? `All (${count})` : `${cfg.label} (${count})`}
                </button>
              );
            })}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button onClick={() => setFitToView(true)}
            style={{ padding: '5px 10px', borderRadius: '999px', border: `1px solid ${theme.border}`, background: fitToView ? theme.glossaryAccent : theme.card, color: fitToView ? '#fff' : theme.textSub, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700 }}>
            Fit
          </button>
          <button onClick={() => { setFitToView(false); setZoom(1); }}
            style={{ padding: '5px 10px', borderRadius: '999px', border: `1px solid ${theme.border}`, background: !fitToView && Math.abs(overviewScale - 1) < 0.02 ? theme.glossaryAccent : theme.card, color: !fitToView && Math.abs(overviewScale - 1) < 0.02 ? '#fff' : theme.textSub, cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700 }}>
            100%
          </button>
          <button onClick={() => { setFitToView(false); setZoom(z => Math.max(0.05, +((fitToView ? overviewScale : z) - 0.25).toFixed(2))); }}
            style={{ padding: '5px 9px', borderRadius: '8px', border: `1px solid ${theme.border}`, background: theme.card, color: theme.textSub, cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700 }}>
            -
          </button>
          <div style={{ minWidth: '62px', textAlign: 'center', fontSize: '0.68rem', fontWeight: 700, color: theme.textSub }}>{actualZoomPct}%</div>
          <button onClick={() => { setFitToView(false); setZoom(z => Math.min(20, +((fitToView ? overviewScale : z) + 0.25).toFixed(2))); }}
            style={{ padding: '5px 9px', borderRadius: '8px', border: `1px solid ${theme.border}`, background: theme.card, color: theme.textSub, cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700 }}>
            +
          </button>
        </div>
      </div>

      {/* Empty state */}
      {events.length === 0 && editId === null && (
        <div style={{ textAlign: 'center', padding: '30px 20px', color: theme.textSub, fontSize: '0.8rem', border: `1px dashed ${theme.border}`, borderRadius: '10px', flexShrink: 0 }}>
          No timeline events yet — click <strong>Add Event</strong> above to add one manually, or run <strong>Extract from Transcript</strong> on the Biography tab to populate automatically.
        </div>
      )}

      {/* ── Main content: horizontal split ── */}
      <div ref={splitContainerRef} style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* ── LEFT: Video + Event list ── */}
        <div ref={leftPanelRef} style={{
          flex: `0 0 ${leftPanelWidth}%`, minWidth: '220px', maxWidth: '55%',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '14px',
        }}>
          {/* Video player */}
          <div style={{ flex: `0 0 ${topPanelPct}%`, minHeight: '80px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '8px 14px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                <Film size={14} strokeWidth={2} style={{ color: theme.glossaryAccent, flexShrink: 0 }} />
                <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: theme.textSub }}>Video Reference</div>
              </div>
              {subtitleLangs.length > 0 && (
                <LanguageSelector
                  languages={subtitleLangs}
                  value={subtitleLang}
                  onChange={setSubtitleLang}
                  theme={theme}
                  compact
                  showOff
                  offLabel="Subtitles Off"
                  label="Subtitles"
                />
              )}
            </div>
            <div style={{ flex: 1, minHeight: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '0 14px 6px', overflow: 'hidden' }}>
              {videoCandidates.length > 0 ? (
                <video key={`${videoId}-pt${activePart}`} ref={videoRef} controls preload="metadata" crossOrigin="anonymous" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '10px', background: '#000', display: 'block' }}
                  onLoadedData={() => {
                    if (!videoRef.current) return;
                    const tracks = videoRef.current.textTracks;
                    for (let i = 0; i < tracks.length; i++) {
                      tracks[i].mode = (subtitleLang !== 'off' && tracks[i].language === subtitleLang) ? 'showing' : 'hidden';
                    }
                  }}>
                  {videoCandidates.map((candidate, i) => (
                    <source key={i} src={encodeVideoPath(activeSubdir, candidate + '.mp4')} type="video/mp4" />
                  ))}
                  {subtitleLang !== 'off' && subtitleTrackUrl && (
                    <track key={subtitleLang} kind="subtitles" src={subtitleTrackUrl} srcLang={subtitleLang} label={getLanguageMeta(subtitleLang).label} default />
                  )}
                </video>
              ) : (
                <div style={{ textAlign: 'center', color: theme.textSub, fontSize: '0.78rem', padding: '20px' }}>
                  No linked video available.
                </div>
              )}
            </div>
            {activePreviewEvent?.description && (
              <div style={{ padding: '0 14px 6px', fontSize: '0.72rem', color: theme.textSub, lineHeight: 1.5, overflowY: 'auto', flexShrink: 0, maxHeight: '60px' }}>
                {activePreviewEvent.description}
              </div>
            )}
          </div>

          {/* ── Vertical divider inside left panel ── */}
          <div
            onPointerDown={onVDividerPointerDown}
            style={{
              flex: '0 0 6px', cursor: 'row-resize',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: '3px',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = `${theme.glossaryAccent}22`; }}
            onMouseLeave={e => { if (!isDraggingRef.current) e.currentTarget.style.background = 'transparent'; }}
          >
            <div style={{ height: '3px', width: '36px', borderRadius: '2px', background: theme.border }} />
          </div>

          {/* Merge controls — contextual, shown only while selecting events to merge.
              The event-data tools (Stamp Times / Clean Locators / AI Review) now live
              in the main Timeline toolbar above. Editors/admins only. */}
          {canEdit && mergeSelection.size > 0 && <div style={{ flexShrink: 0, padding: '6px 10px', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', borderBottom: `1px solid ${theme.border}` }}>
            {mergeSelection.size >= 2 && (
              <button onClick={handleMerge} disabled={merging}
                style={{ padding: '4px 10px', background: '#34c759', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 700, fontSize: '0.68rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', opacity: merging ? 0.6 : 1 }}>
                <GitMerge size={11} strokeWidth={2} /> {merging ? 'Merging...' : `Merge ${mergeSelection.size}`}
              </button>
            )}
            <button onClick={() => setMergeSelection(new Set())}
              style={{ padding: '4px 10px', background: 'none', color: theme.textSub, border: `1px solid ${theme.border}`, borderRadius: '6px', fontSize: '0.68rem', cursor: 'pointer' }}>
              Clear ({mergeSelection.size})
            </button>
          </div>}

          {/* AI Preview banner */}
          {aiPreview && (
            <div style={{ flexShrink: 0, padding: '8px 10px', background: '#8b5cf620', borderBottom: `1px solid #8b5cf644`, fontSize: '0.7rem', color: '#8b5cf6', fontWeight: 700 }}>
              AI suggests {aiPreview.preview_count} events ({aiPreview.merged_count} merges from {aiPreview.original_count} originals) — Approve or Revert in the toolbar above
            </div>
          )}

          {/* Event cards list */}
          <div ref={eventListRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 10px 10px' }}>
            {groupedDecadeCards.map(({ decade, cards }) => (
              <div key={decade} style={{ marginBottom: '18px' }}>
                <div style={{ fontSize: '0.65rem', fontWeight: 700, color: theme.textSub, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ flex: 1, height: '1px', background: theme.border }} />
                  {decade > 0 ? `${decade}s` : 'Unknown date'}
                  <div style={{ flex: 1, height: '1px', background: theme.border }} />
                </div>

                <div style={{ position: 'relative', paddingLeft: '34px' }}>
                  <div style={{ position: 'absolute', left: '21px', top: 0, bottom: 0, width: '2px', background: theme.border, borderRadius: '1px' }} />

                  {cards.map((card, idx) => {
                    const lead = card.items[0];
                    const cfg = TIMELINE_CATEGORIES[lead.category] || TIMELINE_CATEGORIES.other;
                    const isCardActive = card.items.some(item => item.id === activePreviewEvent?.id);
                    // Timeline events carry year/source_locator but never a numeric timestamp,
                    // so a card click only scrolls the overview; seeking is done from the
                    // per-locator "Video" pills (seekVideoToLocator).
                    return (
                      <div
                        key={`${decade}-${card.yearKey}`}
                        data-event-id={lead.id}
                        onClick={() => selectAndScrollToOverview(lead)}
                        role="button"
                        tabIndex={0}
                        aria-label={`${card.yearKey} — ${lead.title || cfg.label}`}
                        onKeyDown={e => {
                          // Only act on the card itself: Enter/Space on a nested
                          // button (merge checkbox, Video/Audio pills) bubbles here.
                          if (e.target !== e.currentTarget) return;
                          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectAndScrollToOverview(lead); }
                        }}
                        style={{ position: 'relative', marginBottom: idx < cards.length - 1 ? '10px' : '0', paddingLeft: '14px', cursor: 'pointer' }}
                      >
                        <div style={{ position: 'absolute', left: '-17px', top: '6px', width: '10px', height: '10px', borderRadius: '50%', background: cfg.color, border: `2px solid ${theme.bg || theme.sidebar}`, boxSizing: 'border-box', zIndex: 1 }} />

                        <div style={{
                          background: isCardActive ? `${cfg.color}08` : theme.card,
                          borderTop: `1px solid ${isCardActive ? cfg.color : (card.items.some(item => item.verified) ? `${cfg.color}60` : theme.border)}`,
                          borderRight: `1px solid ${isCardActive ? cfg.color : (card.items.some(item => item.verified) ? `${cfg.color}60` : theme.border)}`,
                          borderBottom: `1px solid ${isCardActive ? cfg.color : (card.items.some(item => item.verified) ? `${cfg.color}60` : theme.border)}`,
                          borderLeft: `3px solid ${cfg.color}`,
                          borderRadius: '8px',
                          padding: '9px 11px',
                          boxShadow: isCardActive ? `0 0 0 2px ${cfg.color}40` : 'none',
                          transition: 'box-shadow 0.2s, border-color 0.2s, background 0.2s',
                          fontSize: theme.fsCardText
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.78rem', color: cfg.color }}>{card.yearKey}</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '0.62rem', fontWeight: 700, color: cfg.color, background: cfg.bg, padding: '1px 7px', borderRadius: '10px' }}>
                                  {card.items.length === 1 ? cfg.label : `${card.items.length} Events`}
                                </span>
                                {lead.location && (
                                  <span style={{ fontSize: '0.68rem', color: theme.textSub, display: 'flex', alignItems: 'center', gap: '3px' }}>
                                    <MapPin size={9} strokeWidth={2} />{lead.location}
                                  </span>
                                )}
                                {card.items.every(item => item.verified) && (
                                  <span style={{ fontSize: '0.62rem', color: '#34c759', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                    <Check size={10} strokeWidth={2.5} /> Verified
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div style={{ marginTop: '6px' }}>
                            {card.items.map((item, itemIdx) => {
                              const itemCfg = TIMELINE_CATEGORIES[resolveCategory(item.category)] || TIMELINE_CATEGORIES.other;
                              return (
                                <div key={item.id} style={mergeSelection.has(item.id) ? { background: '#34c75910', borderRadius: '6px', padding: '4px', margin: '-4px', marginBottom: itemIdx < card.items.length - 1 ? '4px' : '0' } : {}}>
                                  {itemIdx > 0 && !mergeSelection.has(item.id) && <div style={{ height: '1px', background: theme.border, margin: '8px 0' }} />}
                                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                                    {/* Per-event merge checkbox (editors/admins only) */}
                                    {canEdit && <div
                                      onClick={e => { e.stopPropagation(); toggleMergeSelect(item.id); }}
                                      role="checkbox"
                                      aria-checked={mergeSelection.has(item.id)}
                                      aria-label={`Select "${item.title}" for merge`}
                                      tabIndex={0}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); toggleMergeSelect(item.id); }
                                      }}
                                      style={{ flexShrink: 0, marginTop: '2px', cursor: 'pointer', color: mergeSelection.has(item.id) ? '#34c759' : theme.textSub, opacity: mergeSelection.has(item.id) ? 1 : 0.35 }}
                                      title="Select for merge"
                                    >
                                      {mergeSelection.has(item.id) ? <CheckSquare size={13} strokeWidth={2} /> : <Square size={13} strokeWidth={2} />}
                                    </div>}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontWeight: 700, fontSize: `calc(${theme.fsCardText} + 1px)` }}>{item.title}</div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: '0.62rem', fontWeight: 700, color: itemCfg.color, background: itemCfg.bg, padding: '1px 7px', borderRadius: '10px' }}>{itemCfg.label}</span>
                                        {item.location && (
                                          <span style={{ fontSize: '0.68rem', color: theme.textSub, display: 'flex', alignItems: 'center', gap: '3px' }}>
                                            <MapPin size={9} strokeWidth={2} />{item.location}
                                          </span>
                                        )}
                                        {item.verified && (
                                          <span style={{ fontSize: '0.62rem', color: '#34c759', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                            <Check size={10} strokeWidth={2.5} /> Verified
                                          </span>
                                        )}
                                        {/* Origin and human review are separate facts: an AI-drafted
                                            event a person has checked shows both, rather than the
                                            edit erasing that the AI wrote it. */}
                                        <span style={{ fontSize: '0.55rem', fontWeight: 700, padding: '1px 6px', borderRadius: '10px', opacity: 0.7,
                                          color: item.source === 'manual' ? '#007aff' : theme.textSub,
                                          background: item.source === 'manual' ? '#007aff14' : `${theme.border}44`,
                                          border: `1px solid ${item.source === 'manual' ? '#007aff33' : theme.border}`,
                                        }}>
                                          {item.source === 'manual' ? 'MANUAL' : 'AI'}
                                        </span>
                                        {item.reviewed_by && (
                                          <span title={`Reviewed by ${item.reviewed_by}${item.reviewed_at ? ` · ${String(item.reviewed_at).slice(0, 10)}` : ''}`}
                                            style={{ fontSize: '0.55rem', fontWeight: 700, padding: '1px 6px', borderRadius: '10px',
                                              color: '#34c759', background: '#34c75914', border: '1px solid #34c75933',
                                              display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                            <UserCheck size={9} strokeWidth={2.5} /> HUMAN REVIEWED
                                          </span>
                                        )}
                                      </div>
                                      {item.description && (
                                        <div style={{ fontSize: theme.fsCardText, color: theme.text, marginTop: '5px', lineHeight: 1.5 }}>{item.description}</div>
                                      )}
                                      {item.source_quote && (
                                        <div style={{ fontSize: theme.fsCardText, color: theme.textSub, marginTop: '5px', fontStyle: 'italic', borderLeft: `2px solid ${itemCfg.color}40`, paddingLeft: '7px' }}>
                                          "{item.source_quote}"
                                        </div>
                                      )}
                                      {item.source_locator && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '5px', flexWrap: 'wrap' }}>
                                          {item.source_locator.split(';').map((loc, li) => {
                                            const trimmed = loc.trim();
                                            if (!trimmed) return null;
                                            return (
                                              <span key={li} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                <PlayButton sourceLocator={trimmed} theme={theme} size="small" label="Audio" />
                                                <button
                                                  onClick={e => { e.stopPropagation(); seekVideoToLocator(trimmed); }}
                                                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '6px', fontSize: '0.5rem', fontWeight: 700, cursor: 'pointer', color: '#ef4444', background: '#ef444414', border: '1px solid #ef444433' }}
                                                  title="Seek video to this timestamp">
                                                  <Play size={10} strokeWidth={2.5} />
                                                  Video
                                                </button>
                                                <span style={{ fontSize: '0.56rem', fontFamily: 'monospace', color: theme.textSub }}>{trimmed}</span>
                                              </span>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                    {canEdit && !item._family && <div style={{ display: 'flex', gap: '3px', flexShrink: 0 }}>
                                      <button onClick={e => { e.stopPropagation(); toggleVerify(item); }} title={item.verified ? 'Mark unverified' : 'Mark verified'}
                                        style={{ background: 'none', border: `1px solid ${theme.border}`, borderRadius: '5px', padding: '3px 6px', cursor: 'pointer', color: item.verified ? '#34c759' : theme.textSub, fontSize: '0.65rem' }}>
                                        {item.verified ? <UserCheck size={11} strokeWidth={2} /> : <UserCheck size={11} strokeWidth={2} style={{ opacity: 0.4 }} />}
                                      </button>
                                      <button onClick={e => { e.stopPropagation(); openEdit(item); }}
                                        style={{ background: 'none', border: `1px solid ${theme.border}`, borderRadius: '5px', padding: '3px 6px', cursor: 'pointer', color: theme.textSub }}>
                                        <Edit3 size={11} strokeWidth={2} />
                                      </button>
                                    </div>}
                                  </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Draggable column divider ── */}
        <div
          onPointerDown={onDividerPointerDown}
          style={{
            flex: '0 0 6px', cursor: 'col-resize',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '3px',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = `${theme.glossaryAccent}22`; }}
          onMouseLeave={e => { if (!isDraggingRef.current) e.currentTarget.style.background = 'transparent'; }}
        >
          <div style={{ width: '3px', height: '36px', borderRadius: '2px', background: theme.border }} />
        </div>

        {/* ── RIGHT: Timeline overview (full height) ── */}
        <div ref={rightPanelRef} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Overview visualization */}
          {overviewRange && (
            <div style={{ flex: 1, minHeight: '80px', display: 'flex', overflow: 'hidden' }}>
              <div ref={overviewPaneRef} style={{ flex: 1, minHeight: 0, background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '12px', padding: '18px 20px', overflowX: 'auto', overflowY: 'auto' }}>
                <div style={{ minWidth: `${overviewRenderedWidth}px`, width: 'max-content', margin: '0 auto', position: 'relative', height: `${overviewHeight * overviewScale}px` }}>
                <div style={{ position: 'relative', width: `${overviewBaseWidth}px`, height: `${overviewHeight}px`, transform: `scale(${overviewScale})`, transformOrigin: 'top left' }}>
                <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', zIndex: 0 }}>
                  <line x1="50" y1={timelineY} x2={overviewBaseWidth - 50} y2={timelineY} stroke={theme.border} strokeWidth="3" strokeLinecap="round" />
                  {decadeMarkers.map(year => {
                    const pct = ((year - overviewRange.min) / (overviewRange.max - overviewRange.min)) * 100;
                    const x = 50 + pct * ((overviewBaseWidth - 100) / 100);
                    return (
                      <line key={`decade-line-${year}`} x1={x} y1={markerTop + 22} x2={x} y2={timelineY - 8} stroke={theme.textSub} strokeWidth="1.5" strokeDasharray="3 5" opacity="0.45" />
                    );
                  })}
                  {overviewLayout.map(({ group, x, centerX, lineTop, lineBottom, connectorY }) => {
                    const cfg = TIMELINE_CATEGORIES[group.category] || TIMELINE_CATEGORIES.other;
                    return (
                      <g key={`ov-${group.sortYear}-${group.category}`}>
                        <line x1={x} y1={lineTop} x2={x} y2={lineBottom} stroke={cfg.color} strokeWidth="2.5" strokeLinecap="round" />
                        {centerX !== x && (
                          <line x1={x} y1={connectorY} x2={centerX} y2={connectorY} stroke={cfg.color} strokeWidth="2.5" strokeLinecap="round" />
                        )}
                        <circle cx={x} cy={timelineY} r="6" fill={cfg.color} stroke={theme.card} strokeWidth="2" />
                      </g>
                    );
                  })}
                </svg>
                {decadeMarkers.map(year => {
                  const pct = ((year - overviewRange.min) / (overviewRange.max - overviewRange.min)) * 100;
                  const x = 50 + pct * ((overviewBaseWidth - 100) / 100);
                  return (
                    <div key={`decade-marker-${year}`} style={{ position: 'absolute', left: x, top: markerTop, transform: 'translateX(-50%)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '4px 10px', borderRadius: '999px', background: 'rgba(148,163,184,0.16)', color: theme.textSub, fontSize: '0.62rem', fontWeight: 800, whiteSpace: 'nowrap', border: `1px solid ${theme.border}` }}>
                      {year}s
                    </div>
                  );
                })}
                {overviewLayout.map(({ group, centerX, top }) => {
                  const lead = group.items[0];
                  const cfg = TIMELINE_CATEGORIES[group.category] || TIMELINE_CATEGORIES.other;
                  const isActive = group.items.some(i => i.id === activePreviewEvent?.id);
                  return (
                    <div key={`overview-card-${group.sortYear}-${group.category}`} data-overview-year={group.sortYear} data-overview-id={lead.id}
                      ref={el => { const k = `${group.sortYear}|${group.category}`; if (el) overviewCardRefs.current[k] = el; else delete overviewCardRefs.current[k]; }}
                      onClick={() => selectAndScrollToEvent(lead)}
                      role="button"
                      tabIndex={0}
                      aria-label={`${group.sortYear} ${cfg.label} — ${group.items.length === 1 ? lead.title : `${group.items.length} events`}`}
                      onKeyDown={e => {
                        if (e.target !== e.currentTarget) return;
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectAndScrollToEvent(lead); }
                      }}
                      style={{ position: 'absolute', left: centerX, top, transform: 'translateX(-50%)', width: '176px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', transition: 'filter 0.2s', zIndex: 1 }}>
                      {/* The year label has to be opaque: the tint alone (8% alpha) let
                          every connector running past show straight through the text. */}
                      <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px', padding: '4px 10px', borderRadius: '999px', background: isActive ? cfg.color : `linear-gradient(${cfg.bg}, ${cfg.bg}), ${theme.card}`, border: `1px solid ${isActive ? cfg.color : `${cfg.color}55`}`, color: isActive ? '#fff' : cfg.color, fontSize: '0.62rem', fontWeight: 800, whiteSpace: 'nowrap', maxWidth: '176px', overflow: 'hidden', textOverflow: 'ellipsis', margin: '0 auto', transition: 'background 0.2s, color 0.2s' }}>
                        <span>{group.sortYear}</span>
                        <span>{cfg.label}{group.items.length > 1 ? ` · ${group.items.length}` : ''}</span>
                      </div>
                      {/* Outline in the category colour, so a card, its label and the
                          connector that runs down to its year all read as one thing. */}
                      <div style={{ marginTop: '6px', width: '100%', background: theme.bg || theme.sidebar, border: isActive ? `2px solid ${cfg.color}` : `1px solid ${cfg.color}55`, borderRadius: '10px', padding: '6px 8px', transition: 'border 0.2s, box-shadow 0.2s', boxShadow: isActive ? `0 0 12px ${cfg.color}30` : 'none' }}>
                        {group.items.map((item, itemIdx) => (
                          <div key={item.id} data-overview-item-id={item.id} onClick={e => { e.stopPropagation(); selectAndScrollToEvent(item); }}
                            role="button"
                            tabIndex={0}
                            aria-label={item.title}
                            onKeyDown={e => {
                              if (e.target !== e.currentTarget) return;
                              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); selectAndScrollToEvent(item); }
                            }}
                            style={{ cursor: 'pointer', borderRadius: '6px', padding: '3px 5px', margin: '-2px -4px',
                              background: activePreviewEvent?.id === item.id ? `${cfg.color}18` : 'transparent',
                              border: activePreviewEvent?.id === item.id ? `1px solid ${cfg.color}40` : '1px solid transparent',
                              transition: 'background 0.2s, border-color 0.2s' }}>
                            {itemIdx > 0 && <div style={{ height: '1px', background: `${cfg.color}30`, margin: '5px 0' }} />}
                            <div style={{ fontSize: '0.67rem', fontWeight: 700, color: activePreviewEvent?.id === item.id ? cfg.color : theme.text, lineHeight: 1.25, textAlign: 'center' }}>{item.title}</div>
                            {item.source_locator && (
                              <div style={{ display: 'flex', justifyContent: 'center', gap: '3px', marginTop: '3px', flexWrap: 'wrap' }}>
                                {(() => {
                                  const locs = item.source_locator.split(';').map(s => s.trim()).filter(Boolean);
                                  return locs.map((trimmed, li) => {
                                    // An event that carries on across a tape change keeps one
                                    // span per tape. Unlabelled, those read as duplicate
                                    // buttons, so name the tape whenever there is more than one.
                                    const m = trimmed.match(/^(.*?)\s*\d{1,2}:\d{2}:\d{2}/);
                                    const tape = m ? m[1].trim().replace(/^tape\s*/i, 'T') : '';
                                    return (
                                      <span key={li} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }} title={trimmed}>
                                        {locs.length > 1 && tape && (
                                          <span style={{ fontSize: '0.45rem', fontWeight: 800, fontFamily: 'monospace', color: theme.textSub }}>{tape}</span>
                                        )}
                                        <PlayButton sourceLocator={trimmed} theme={theme} size="small" label="Audio" />
                                        <button
                                          onClick={e => { e.stopPropagation(); seekVideoToLocator(trimmed); }}
                                          style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '1px 6px', borderRadius: '6px', fontSize: '0.45rem', fontWeight: 700, cursor: 'pointer', color: '#ef4444', background: '#ef444414', border: '1px solid #ef444433' }}>
                                          <Play size={8} strokeWidth={2.5} /> Video
                                        </button>
                                      </span>
                                    );
                                  });
                                })()}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {/* Top layer. A card that could not sit on its own year is joined to it
                    by a long horizontal run, which crosses the year labels of every
                    other card in the same row. Those labels are opaque and win, so the
                    same run is redrawn here as dots: over its own solid line it is the
                    same colour and invisible, over a label it reads as a dotted
                    crossing, so the line stays traceable and the label stays legible. */}
                <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', zIndex: 2, pointerEvents: 'none' }}>
                  {overviewLayout.map(({ group, x, centerX, connectorY }) => {
                    if (centerX === x) return null;
                    const cfg = TIMELINE_CATEGORIES[group.category] || TIMELINE_CATEGORIES.other;
                    return (
                      <line key={`ov-dot-${group.sortYear}-${group.category}`} x1={x} y1={connectorY} x2={centerX} y2={connectorY}
                        stroke={cfg.color} strokeWidth="2.5" strokeLinecap="round" strokeDasharray="1 6" opacity="0.75" />
                    );
                  })}
                </svg>
                </div>
                </div>
              </div>
            </div>
          )}

          {!overviewRange && (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textSub, fontSize: '0.8rem' }}>
              No events to visualize yet.
            </div>
          )}
        </div>
      </div>
      </>)}

      {/* ── MODAL: Event Editor ── */}
      {editId !== null && (
        <div
          onClick={cancel}
          style={{
            position: 'fixed', inset: 0, zIndex: Z.modal,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '24px',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: '680px', maxHeight: '92vh',
              overflowY: 'auto',
              background: theme.card,
              borderRadius: '16px',
              padding: '18px',
              boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
            }}
          >
            {renderEventEditor('top')}
          </div>
        </div>
      )}
    </div>
  );
}
